import React from 'react';

/**
 * Level Badge Component
 * Displays a user's level badge in the top-left corner of their banner
 * @param {number} level - Current level (1-100)
 * @param {string} className - Additional CSS classes
 * @param {boolean} small - Use smaller size
 * @param {boolean} verified - Show verified checkmark
 */
const LevelBadge = ({ level = 1, className = '', small = false, verified = false }) => {
  return (
    <div 
      className={`absolute ${small ? '-top-0.5 left-1 text-[13px] px-1 py-0' : 'top-3 left-1 text-3xl px-1 py-0'} text-white font-bold digital-font drop-shadow-lg flex items-center ${className}`}
      style={{ zIndex: 10 }}
    >
      Lv {level}
      {verified && (
        <svg className={`${small ? 'w-3 h-3 ml-0.5' : 'w-5 h-5 ml-1'} text-blue-500`} fill="currentColor" viewBox="0 0 20 20" title="Verified Account">
          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      )}
    </div>
  );
};

/**
 * EXP Progress Bar Component  
 * Displays an EXP progress bar at the bottom of a player banner
 * @param {number} level - Current level (determines exp needed for next level)
 * @param {number} exp - Current EXP within the level
 * @param {string} className - Additional CSS classes
 * @param {boolean} lower - Position it lower for top banner
 */
export const ExpProgressBar = ({ level = 1, exp = 0, className = '', lower = false }) => {
  // Calculate EXP needed for next level: Level N needs N × 100 BP
  const expNeeded = level * 100;
  
  // Calculate progress percentage (0-100)
  const progress = level >= 100 ? 100 : Math.min((exp / expNeeded) * 100, 100);
  
  return (
    <>
      {/* Exp Label - Bottom Left (Centered with bar) */}
      <span className={`absolute ${lower ? 'bottom-1' : 'bottom-[3px]'} left-2 text-[10px] text-slate-400 font-semibold`} style={{ zIndex: 10 }}>
        Exp
      </span>
      
      {/* Progress Bar - Centered Between Banner Edges */}
      <div 
        className={`absolute ${lower ? 'bottom-1' : 'bottom-1'} h-1.5 bg-slate-900/50 rounded-full overflow-hidden ${className}`}
        style={{ left: lower ? '12%' : '9.5%', right: lower ? '12%' : '9.5%', zIndex: 10 }}
      >
        <div 
          className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-500 ease-out rounded-full"
          style={{ width: `${progress}%` }}
        />
      </div>
    </>
  );
};

export default LevelBadge;
